import { Controller } from "@hotwired/stimulus"

// The public bar's behaviour: the menu sheet below 1000px, and the scrolled
// frost (docs/07 Mobile header).
//
// This is the design system's `system/nav.js`, re-expressed as a Stimulus
// controller rather than imported. Not a rewrite of its DESIGN — the classes
// it toggles (`navopen`, `scrolled`) are the design's and nav.css is still the
// authority for what they look like — but its IIFE captures `#topbar` and
// `#navtoggle` once at load, and a Turbo visit replaces both. The menu would
// work on the first page a visitor landed on and silently stop working on
// every page after it, which is the kind of bug nobody reports because it
// looks like the site simply has no menu.
//
// Stimulus reconnects on every visit, which is the whole reason D29 keeps our
// behaviour here and the design's artwork in its own files.
export default class extends Controller {
  static targets = ["sheet", "toggle"]

  // ONE sheet, one owner (P10.18).
  //
  // The design's carried `page-body.js` ends with a copy of this behaviour —
  // the same `navopen` class on the same `#topbar`, bound to the same
  // `#navtoggle`, with the same document-level Escape and Tab trap. Two
  // handlers on one click toggle twice and the sheet never opens; two Tab
  // traps advance focus two links at a time.
  //
  // It was invisible until home's zone rail was rebuilt from the approved
  // template. page-body.js:539 reads `.zrail`, our markup rendered `.zsteps`,
  // so the script threw a TypeError there and EVERYTHING below it — the nav
  // block included — never ran. Giving the rail its real class brought the
  // duplicate to life.
  //
  // So where that script is on the page it owns the sheet, and this stands
  // down. It is home alone at this pin, and only the sheet: the scrolled
  // frost below is ours on every page.
  //
  // The real fix is one implementation, not two. That means either not
  // carrying page-body.js's nav block or dropping this controller's sheet
  // half, and the choice belongs with whoever owns the design sync.
  get designOwnsTheSheet() {
    return document.querySelector('script[src^="/design/home/page-body.js"]') !== null
  }

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onBreakpoint = this.onBreakpoint.bind(this)

    if (!this.designOwnsTheSheet) document.addEventListener("keydown", this.onKeydown)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.breakpoint = window.matchMedia("(max-width:1000px)")
    this.breakpoint.addEventListener("change", this.onBreakpoint)

    // A page restored mid-scroll must start frosted, not bare.
    this.onScroll()
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("scroll", this.onScroll)
    this.breakpoint?.removeEventListener("change", this.onBreakpoint)
    // Never leave the page locked behind a sheet that no longer exists.
    document.body.style.overflow = ""
  }

  get open() { return this.element.classList.contains("navopen") }

  toggle() {
    if (this.designOwnsTheSheet) return

    this.open ? this.close() : this.show()
  }

  show() {
    this.element.classList.add("navopen")
    this.toggleTarget.setAttribute("aria-expanded", "true")
    document.body.style.overflow = "hidden"
    // Focus moves in, or a keyboard user opens the menu and stays behind it.
    this.sheetTarget.querySelector("a")?.focus()
  }

  // `returnFocus` only when the person closed it deliberately — Escape or the
  // button. Following a link closes the sheet on the way out, and focusing the
  // button then would fight the new page for focus.
  close(returnFocus = false) {
    if (!this.open) return

    this.element.classList.remove("navopen")
    this.toggleTarget.setAttribute("aria-expanded", "false")
    document.body.style.overflow = ""
    if (returnFocus) this.toggleTarget.focus()
  }

  // A tap on a link navigates; close first so a same-page anchor is not left
  // under an open sheet.
  closeOnNavigate(event) {
    if (this.designOwnsTheSheet) return
    if (event.target.closest("a")) this.close()
  }

  onKeydown(event) {
    if (!this.open) return

    if (event.key === "Escape") {
      event.preventDefault()
      return this.close(true)
    }
    if (event.key !== "Tab") return

    // The trigger and the links are the loop. There is no "tap outside to
    // close": the sheet is full-screen, so there is no outside.
    const items = [ this.toggleTarget, ...this.sheetTarget.querySelectorAll("a") ]
    const here = items.indexOf(document.activeElement)
    if (here < 0) return

    event.preventDefault()
    const next = event.shiftKey ? here - 1 : here + 1
    items[(next + items.length) % items.length].focus()
  }

  onScroll() {
    this.element.classList.toggle("scrolled", (window.scrollY || 0) > 10)
  }

  // Growing past the breakpoint restores the inline nav; the sheet must not be
  // left open behind it, with the page still locked.
  onBreakpoint() {
    if (!this.breakpoint.matches) this.close()
  }
}
